<template>
  <b-modal id="zwolnienie-modal" size="lg" hide-footer title="Zwolnienie lekarskie (e-ZLA)" @show="przygotuj()">
    <b-form @submit.prevent="handleSubmit()">
      <b-alert v-if="!icd10" show variant="warning" class="mb-3">
        <strong>Najpierw rozpoznanie.</strong>
        Zwolnienia nie da się wystawić bez kodu ICD-10 — ZUS wymaga go
        w każdym zaświadczeniu lekarskim.
        <div class="mt-1">
          Uzupełnij rozpoznanie w opisie wizyty albo wybierz kod poniżej.
          Gdy tylko pojawi się przy wizycie, podpowiemy go tutaj sami.
        </div>
      </b-alert>
      <b-row>
        <b-col cols="4">
          <b-form-group label="Liczba dni">
            <b-form-input
              type="number"
              min="1"
              max="182"
              v-model.number="liczbaDni"
              @input="przelicz()"
            />
          </b-form-group>
        </b-col>
        <b-col cols="4">
          <b-form-group label="Niezdolność od">
            <b-form-input type="date" v-model="dataOd" @input="przelicz()" />
          </b-form-group>
        </b-col>
        <b-col cols="4">
          <b-form-group label="Niezdolność do">
            <b-form-input type="date" v-model="dataDo" />
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="Rozpoznanie ICD-10 (wymagane)">
        <complete-select
          @select="handleSelectIcd($event)"
          @input="handleSearchIcd"
          :data="icd10Options"
          id="icd10-zla"
          placeholder="Wpisz kod lub nazwę ICD-10"
        />
        <small v-if="icd10" class="text-muted">
          Wybrane: <strong>{{ icd10 }}</strong>
          <span v-if="icd10ZWizyty"> — podpowiedziane z opisu wizyty, potwierdź lub zmień</span>
        </small>
      </b-form-group>

      <b-form-group label="Wskazanie lekarskie">
        <b-form-radio-group v-model="wskazanie" :options="wskazania" />
      </b-form-group>

      <b-form-group label="Kody literowe">
        <b-form-checkbox-group v-model="kodyLiterowe" :options="kody" />
      </b-form-group>

      <b-form-group label="Płatnik (pracodawca)">
        <b-row>
          <b-col cols="5">
            <b-form-input v-model="platnikNip" placeholder="NIP płatnika" />
          </b-col>
          <b-col cols="7">
            <b-form-input v-model="platnikNazwa" placeholder="Nazwa pracodawcy" />
          </b-col>
        </b-row>
        <small class="text-muted">
          Potrzebne tylko do sprawdzenia kompletności. Przy wystawianiu płatnika
          pobieramy wprost z ZUS po numerze PESEL pacjenta. Przy kilku
          pracodawcach wystawia się <strong>osobne zwolnienie dla każdego</strong>.
        </small>
      </b-form-group>

      <!--
        Hasło certyfikatu jest tu ZAWSZE — inaczej niż przy skierowaniach
        i receptach, gdzie pole pokazuje się tylko, gdy lekarz nie ma
        zapisanego hasła. Zwolnienie podpisuje się ręcznie za każdym razem.
      -->
      <b-form-group :label="$t('visit.certPassword')">
        <b-form-input type="password" v-model="haslo" autocomplete="off" />
        <small class="text-muted">
          Zwolnienie podpisuje się <strong>ręcznie przy każdym dokumencie</strong> —
          nie korzystamy z hasła zapisanego przy koncie.
        </small>
      </b-form-group>

      <b-alert v-if="sprawdzony === true" show variant="success">
        <strong>Dokument kompletny.</strong>
        <span v-if="podsumowanie"> Okres i rozpoznanie: {{ podsumowanie }}.</span>
        Brakuje już tylko podpisu.
      </b-alert>
      <b-alert v-if="braki.length" show variant="danger">
        <strong>Braki blokujące wystawienie:</strong>
        <ul class="mb-0">
          <li v-for="(b, i) in braki" :key="i">{{ b.opis }}</li>
        </ul>
      </b-alert>

      <b-alert v-if="pytania.length" show variant="info">
        <strong>System pyta, zamiast zgadywać:</strong>
        <ul class="mb-0">
          <li v-for="(p, i) in pytania" :key="i">{{ p }}</li>
        </ul>
      </b-alert>

      <b-alert v-if="bladApi" show variant="warning">{{ bladApi }}</b-alert>

      <!--
        ZUS zna podobne zwolnienia tego pacjenta. To NIE jest błąd i nie
        wolno tego chować: drugie zwolnienie na ten sam okres bywa uzasadnione
        (inny pracodawca, inna choroba). Decyzję podejmuje lekarz, dlatego
        wystawienie wymaga tu drugiego, świadomego kliknięcia.
      -->
      <b-alert v-if="podobne.length" show variant="warning">
        <strong>Ten pacjent ma już zwolnienia w ZUS:</strong>
        <ul class="mb-2">
          <li v-for="(p, i) in podobne" :key="i">
            {{ p.seria }} {{ p.numer }}: {{ p.od }} → {{ p.do }}
            <span v-if="p.lekarz">, {{ p.lekarz }}</span>
            <span v-if="p.status"> ({{ p.status }})</span>
          </li>
        </ul>
        <b-button size="sm" variant="warning" :disabled="zajety || !haslo" @click="wystaw(true)">
          Wystaw mimo to
        </b-button>
        <small class="d-block mt-1">
          Podaj hasło ponownie — po każdej próbie jest czyszczone.
        </small>
      </b-alert>

      <b-alert v-if="wystawione" show :variant="wystawione.zapisano_u_nas === false ? 'warning' : 'success'">
        <strong>Zwolnienie wystawione: {{ wystawione.seria_numer }}.</strong>
        Ubezpieczony: {{ wystawione.ubezpieczony.imie }} {{ wystawione.ubezpieczony.nazwisko }}.
        Płatnik: {{ wystawione.platnik.nazwa }}.
        <div v-if="wystawione.srodowisko === 'symulator'" class="mt-1">
          <strong>Środowisko testowe ZUS</strong> — to nie jest prawdziwe zwolnienie.
        </div>
        <!--
          Zapis w rejestrze placówki mógł się nie udać, choć ZUS dokument już ma.
          Wtedy numer z tego ekranu jest JEDYNYM śladem u nas — mówimy to wprost,
          zamiast chować ostrzeżenie za zielonym komunikatem.
        -->
        <div v-if="wystawione.ostrzezenie" class="mt-2">
          <strong>Uwaga:</strong> {{ wystawione.ostrzezenie }}
        </div>
        <div v-if="wystawione.zapisano_u_nas" class="mt-2">
          <b-button size="sm" variant="primary" :disabled="!!pobieranie" @click="pobierz('wydruk')">
            {{ pobieranie === 'wydruk' ? 'Przygotowuję wydruk…' : 'Wydruk dla pacjenta (PDF)' }}
          </b-button>
          <b-button
            size="sm"
            variant="outline-secondary"
            class="ml-2"
            :disabled="!!pobieranie"
            @click="pobierz('upp')"
          >
            Poświadczenie z ZUS (XML)
          </b-button>
          <small class="d-block mt-1">
            Wydruk jest na życzenie pacjenta — ZUS sam dostarcza zwolnienie pracodawcy.
            Rozpoznanie nie jest na nim drukowane.
          </small>
        </div>
        <div v-if="bladPobrania" class="mt-2 text-danger">{{ bladPobrania }}</div>
        <small class="d-block mt-2">
          Dokumentu nie da się już poprawić. Korekta oznacza anulowanie
          i wystawienie nowego (ekran Integracje ZUS i NFZ).
        </small>
      </b-alert>

      <b-row>
        <b-col>
          <b-button
            variant="outline-secondary"
            @click="sprawdz()"
            :disabled="zajety || !icd10"
            :title="!icd10 ? 'Najpierw uzupełnij rozpoznanie' : ''"
          >
            Sprawdź kompletność
          </b-button>
        </b-col>
        <b-col style="text-align: right">
          <b-button
            size="lg"
            type="submit"
            variant="primary"
            :disabled="zajety || !haslo || !icd10 || braki.length > 0 || !!wystawione"
            :title="podpowiedzPrzycisku"
          >
            {{ zajety ? 'Wysyłam do ZUS…' : 'Podpisz i wyślij do ZUS' }}
          </b-button>
        </b-col>
      </b-row>
      <small class="text-muted d-block mt-2">
        Po wysłaniu dokument istnieje w rejestrze ZUS i nie da się go poprawić.
        Wystawienie zatrzyma się, jeśli pacjent ma już podobne zwolnienie.
      </small>
    </b-form>
  </b-modal>
</template>

<script>
/**
 * Modal wystawiania zwolnienia — w prawym panelu, obok recept i skierowań.
 *
 * DLACZEGO MODAL, A NIE ZAKŁADKA W LEWYM PANELU: lewy panel to archiwum
 * pacjenta (co było), a zwolnienie się WYSTAWIA przy bieżącej wizycie.
 * Dlatego wchodzi tą samą drogą co recepta i skierowanie — ikoną z paska
 * nad opisem wizyty.
 *
 * REGUŁY LICZY MEDAPI, NIE TEN KOMPONENT. Limity okresu, wymagalność pól
 * i lista braków przychodzą z `/api/auth/ezla/draft` (proxy do medapi),
 * żeby nie mieć dwóch rozjeżdżających się implementacji przepisów ZUS.
 */
import { mapState } from 'vuex'
import CompleteSelect from '@/components/CompleteSelect.vue'
import lookups from '@/services/lookups'
import ezla from '@/services/ezla'

export default {
  name: 'ZwolnienieModal',
  components: {
    CompleteSelect
  },
  data() {
    return {
      liczbaDni: 3,
      dataOd: new Date().toISOString().slice(0, 10),
      dataDo: '',
      icd10: '',
      icd10ZWizyty: false,
      icdList: [],
      wskazanie: 'powinien lezec',
      wskazania: [
        { text: 'Powinien leżeć', value: 'powinien lezec' },
        { text: 'Może chodzić', value: 'moze chodzic' }
      ],
      kodyLiterowe: [],
      kody: ['A', 'B', 'C', 'D', 'E'],
      platnikNip: '',
      platnikNazwa: '',
      haslo: '',
      braki: [],
      pytania: [],
      // null = jeszcze nie sprawdzano; true/false = wynik ostatniego sprawdzenia
      sprawdzony: null,
      podsumowanie: '',
      bladApi: '',
      zajety: false,
      /** Lista z ZUS, gdy pacjent ma już podobne zwolnienia (odpowiedź 409). */
      podobne: [],
      /** Wynik udanego wystawienia — seria i numer nadane przez ZUS. */
      wystawione: null,
      /** Który plik właśnie się pobiera: 'wydruk' | 'upp' | ''. */
      pobieranie: '',
      bladPobrania: ''
    }
  },
  computed: {
    ...mapState('Visit', {
      currentVisit: (state) => state.currentVisit
    }),
    ...mapState('Patient', {
      pacjent: (state) => state.patient
    }),
    icd10Options() {
      return this.icdList.map((el) => ({
        label: `${el.code} ${el.name}`,
        value: el,
        $isDisabled: el.code.includes('–')
      }))
    },
    /** Jeden powód blokady zamiast zgadywania, czego brakuje. */
    podpowiedzPrzycisku() {
      if (this.wystawione) return 'To zwolnienie zostało już wystawione'
      if (!this.icd10) return 'Najpierw uzupełnij rozpoznanie'
      if (this.braki.length) return 'Uzupełnij braki blokujące wystawienie'
      if (!this.haslo) return 'Podaj hasło do certyfikatu — zwolnienie podpisuje się ręcznie'
      return ''
    }
  },
  methods: {
    /**
     * Przygotowanie modala przy KAŻDYM otwarciu — wizyta bywa załadowana
     * później niż komponent, więc liczenie w mounted() było za wcześnie.
     */
    przygotuj() {
      this.przelicz()
      this.podpowiedzZWizyty()
    },
    /**
     * Rozpoznanie z opisu biezacej wizyty.
     *
     * UWAGA na ksztalt danych:  bywa zwyklym tekstem,
     * ale bywa tez JSON-em (tablica pozycji z polem ) — tak zapisuje je
     * ekran opisu wizyty. Obslugujemy oba, inaczej podpowiedz milczy dokladnie
     * wtedy, gdy lekarz porzadnie wypelnil rozpoznanie.
     *
     * Szukamy KODU ICD-10, nie zgadujemy rozpoznania z tresci.
     */
    podpowiedzZWizyty() {
      // Kod ICD-10 szukamy w SUROWYM tekście rozpoznania. Pole bywa zwykłym
      // tekstem ("E03.9 Niedoczynność…") albo JSON-em z pozycjami
      // {text, description} — kod występuje w obu postaciach, więc
      // parsowanie JSON-a było zbędnym pośrednikiem, który mógł trafić
      // w złe pole.
      const surowe = String((this.currentVisit && this.currentVisit.diagnosis) || '')
      const m = surowe.match(/\b([A-Z]\d{2}(?:\.\d{1,2})?)\b/)
      if (m && !this.icd10) {
        this.icd10 = m[1]
        this.icd10ZWizyty = true
        this.handleSearchIcd(m[1])
      }
    },
    przelicz() {
      if (!this.dataOd || !this.liczbaDni) return
      const d = new Date(this.dataOd)
      d.setDate(d.getDate() + Number(this.liczbaDni) - 1)
      this.dataDo = d.toISOString().slice(0, 10)
    },
    handleSelectIcd(data) {
      // CompleteSelect emituje option.value — czyli JUŻ obiekt {name, code},
      // nie opakowanie (CompleteSelect.vue:124). Wcześniej czytałem
      // data.value.code i wybór nie ustawiał niczego: kod było widać,
      // ale icd10 zostawało puste i przycisk był zablokowany.
      const kod = data && (data.code || (data.value && data.value.code))
      if (kod) {
        this.icd10 = kod
        this.icd10ZWizyty = false
      }
    },
    async handleSearchIcd(searchText) {
      try {
        const { data } = await lookups.getLookup('icd10', searchText)
        this.icdList = data.map((el) => ({
          name: el.nazwa,
          code: el.kod.toString()
        }))
      } catch (e) {
        this.icdList = []
      }
    },
    /** Płatnik wpisany ręcznie — dopóki nie mamy listy z ZUS. */
    platnicyDoWyslania() {
      if (!this.platnikNip) return []
      return [
        {
          identyfikator: this.platnikNip,
          typIdentyfikatora: 'NIP',
          nazwa: this.platnikNazwa || this.platnikNip
        }
      ]
    },
    async sprawdz() {
      this.zajety = true
      this.bladApi = ''
      this.sprawdzony = null

      // ZUS identyfikuje ubezpieczonego numerem PESEL. Bez niego żądanie
      // i tak wróciłoby z błędem walidacji — lepiej powiedzieć wprost.
      const pesel = String((this.pacjent && this.pacjent.pesel) || '').trim()
      if (!/^\d{11}$/.test(pesel)) {
        this.bladApi =
          'Pacjent nie ma poprawnego numeru PESEL w kartotece. ' +
          'ZUS wymaga go w każdym zaświadczeniu — uzupełnij dane pacjenta.'
        this.zajety = false
        return
      }

      try {
        const { data } = await ezla.draft({
          pacjent: {
            pesel,
            imie: (this.pacjent && this.pacjent.firstName) || '',
            nazwisko: (this.pacjent && this.pacjent.lastName) || ''
          },
          liczba_dni: Number(this.liczbaDni) || undefined,
          data_od: this.dataOd || undefined,
          data_do: this.dataDo || undefined,
          icd10: this.icd10 || undefined,
          wskazanie: this.wskazanie,
          kody_literowe: this.kodyLiterowe,
          platnicy: this.platnicyDoWyslania()
        })
        this.braki = (data.braki || []).filter((b) => b.waga === 'blad')
        this.pytania = data.pytania || []
        this.sprawdzony = this.braki.length === 0
        const o = data.draft && data.draft.okres
        this.podsumowanie = o
          ? `${o.od} → ${o.do}` + (data.draft.icd10 ? `, ${data.draft.icd10}` : '')
          : ''
      } catch (e) {
        // Pokazujemy PRAWDZIWY powód z serwera, a nie ogólnik. Najczęstszy
        // przypadek to brak MEDAPI_API_KEY (503) — komunikat „sprawdź, czy
        // medapi odpowiada" wysyłał wtedy szukającego w złą stronę.
        const odp = e && e.response
        this.bladApi =
          (odp && odp.data && odp.data.message) ||
          (odp
            ? 'Moduł e-ZLA odpowiedział błędem ' + odp.status + '.'
            : 'Brak połączenia z modułem e-ZLA.')
      } finally {
        this.zajety = false
      }
    },
    handleSubmit() {
      this.wystaw(false)
    },
    /**
     * Wystawienie w ZUS.
     *
     * Płatnika, adres i dane ubezpieczonego bierze medapi wprost z ZUS po
     * numerze PESEL — nie wysyłamy tu tego, co lekarz wpisał w pole płatnika,
     * bo rejestr urzędu jest w tej sprawie ważniejszy od naszej kartoteki.
     *
     * `pomimoPodobnych` ustawia się WYŁĄCZNIE po tym, jak lekarz zobaczył
     * listę podobnych zwolnień i kliknął drugi raz.
     */
    async wystaw(pomimoPodobnych) {
      const pesel = String((this.pacjent && this.pacjent.pesel) || '').trim()
      if (!/^\d{11}$/.test(pesel)) {
        this.bladApi =
          'Pacjent nie ma poprawnego numeru PESEL w kartotece. ' +
          'ZUS wymaga go w każdym zaświadczeniu — uzupełnij dane pacjenta.'
        return
      }
      this.zajety = true
      this.bladApi = ''
      this.bladPobrania = ''
      if (!pomimoPodobnych) this.podobne = []
      try {
        const { data } = await ezla.wystaw({
          haslo_certyfikatu: this.haslo,
          pesel,
          data_od: this.dataOd,
          data_do: this.dataDo,
          icd10: this.icd10,
          wskazanie: this.wskazanie,
          kody_literowe: this.kodyLiterowe,
          pomimo_podobnych: !!pomimoPodobnych,
          // Wizytę i pacjenta bierzemy z ADRESU strony, nie ze stanu aplikacji:
          // parametry adresu są napisami, więc nie tracą precyzji. Numery
          // w tej bazie są większe niż zakres dokładny dla liczb w JavaScripcie
          // — jako liczba przypięłyby zwolnienie do cudzej wizyty.
          wiz_id: this.$route.params.visit || undefined,
          pcj_id: this.$route.params.id || undefined
        })
        this.wystawione = data
        this.podobne = []
      } catch (e) {
        const odp = e && e.response
        const dane = (odp && odp.data) || {}
        // 409 to nie awaria, tylko stan po stronie ZUS wymagający decyzji.
        if (odp && odp.status === 409 && dane.podobne) {
          this.podobne = dane.podobne
          this.bladApi = ''
        } else {
          this.bladApi =
            dane.message ||
            (odp
              ? 'Moduł e-ZLA odpowiedział błędem ' + odp.status + '.'
              : 'Brak połączenia z modułem e-ZLA.')
        }
      } finally {
        // Hasło żyje przez jeden podpis. Przy „Wystaw mimo to" lekarz podaje
        // je ponownie — świadomie, bo to druga decyzja, nie powtórzenie tej samej.
        this.haslo = ''
        this.zajety = false
      }
    },
    /**
     * Wydruk dla pacjenta (PDF) albo poświadczenie z ZUS (XML) z rejestru placówki.
     *
     * Plik nie trafia do stanu aplikacji: blob idzie prosto do przeglądarki,
     * a adres tymczasowy jest zwalniany po minucie — dokument z danymi pacjenta
     * nie ma zostawać w pamięci karty dłużej, niż trzeba. Kto pobrał, zapisuje
     * serwer w dzienniku zdarzeń.
     */
    async pobierz(rodzaj) {
      if (!this.wystawione) return
      this.pobieranie = rodzaj
      this.bladPobrania = ''
      try {
        const { data } = await ezla.wydruk({ seria_numer: this.wystawione.seria_numer, rodzaj })
        const adres = URL.createObjectURL(data)
        if (rodzaj === 'wydruk') {
          window.open(adres, '_blank')
        } else {
          const link = document.createElement('a')
          link.href = adres
          link.download = `upp-${this.wystawione.seria_numer}.xml`
          link.click()
        }
        setTimeout(() => URL.revokeObjectURL(adres), 60000)
      } catch (e) {
        // Przy odpowiedzi typu blob błąd serwera TEŻ przychodzi jako blob —
        // bez odczytania go lekarz dostałby ogólnik zamiast prawdziwego powodu
        // (np. „rejestr zwolnień nie jest jeszcze założony").
        const odp = e && e.response
        let komunikat = ''
        try {
          komunikat = JSON.parse(await odp.data.text()).message || ''
        } catch (bladOdczytu) {
          // treść nie była JSON-em — zostaje komunikat ogólny poniżej
        }
        this.bladPobrania =
          komunikat ||
          (odp ? 'Wydruk odpowiedział błędem ' + odp.status + '.' : 'Brak połączenia z modułem e-ZLA.')
      } finally {
        this.pobieranie = ''
      }
    }
  }
}
</script>
